import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { appToast } from '../../../components/AppToast'
import { fetchMarksRoster, saveMarks } from '../tests/api/testApi'

export default function TestAllocationModal({
  isOpen,
  onClose,
  test = null,
  onSuccess,
}) {
  useBodyScrollLock(isOpen)

  const [students, setStudents] = useState([])
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')

  useEffect(() => {
    if (!isOpen || !test?.id) return
    let active = true
    setIsLoading(true)

    fetchMarksRoster(test.id, 'A', 0)
      .then((data) => {
        if (!active) return
        const list = Array.isArray(data?.students) ? data.students : []
        setStudents(list)
        // Select all by default
        const initialSet = new Set(list.map((s) => s.studentId ?? s.id))
        setSelectedIds(initialSet)
      })
      .catch((err) => {
        if (!active) return
        appToast.error(err?.message || 'Failed to load eligible candidates for allocation')
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [isOpen, test?.id])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !test) return null

  const filteredStudents = students.filter((s) => {
    if (!searchFilter.trim()) return true
    const term = searchFilter.toLowerCase()
    const name = (s.fullName || s.name || '').toLowerCase()
    const roll = String(s.rollNo || '')
    const branch = (s.branchName || '').toLowerCase()
    const batch = (s.batchNames || '').toLowerCase()
    return name.includes(term) || roll.includes(term) || branch.includes(term) || batch.includes(term)
  })

  const isAllSelected = filteredStudents.length > 0 && filteredStudents.every((s) => selectedIds.has(s.studentId ?? s.id))

  const handleToggleAll = () => {
    if (isAllSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        filteredStudents.forEach((s) => next.delete(s.studentId ?? s.id))
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        filteredStudents.forEach((s) => next.add(s.studentId ?? s.id))
        return next
      })
    }
  }

  const handleToggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSaveAllocation = async () => {
    if (selectedIds.size === 0) {
      appToast.warning('Please select at least one student to allocate.')
      return
    }

    setIsSaving(true)
    try {
      const marksList = Array.from(selectedIds).map((id) => ({
        studentId: id,
        marks: 0,
        attendance: 'P',
      }))

      await saveMarks(test.id, marksList)
      appToast.success(`Successfully allocated ${selectedIds.size} students for Test #${test.testNo || test.id}`)
      onSuccess?.()
      onClose?.()
    } catch (err) {
      appToast.error(err?.message || 'Failed to allocate students to test')
    } finally {
      setIsSaving(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity animate-in fade-in"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-3xl rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[88vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-xl">assignment_ind</span>
            </div>
            <div>
              <h2 className="font-display text-base font-bold text-on-surface">Allocate Students for Examination</h2>
              <p className="text-xs text-on-surface-variant">
                Test #{test.testNo || test.id} &bull; {test.subject || test.subjectName} ({test.testDate || '—'})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Search & Selection Counts Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 shrink-0">
          <div className="relative flex-1 max-w-sm">
            <span className="material-symbols-outlined absolute left-3 top-2.5 text-base text-on-surface-variant">
              search
            </span>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search candidate students..."
              className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low pl-9 pr-3 text-xs text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
            />
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant">
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-primary">
              {selectedIds.size} of {students.length} Selected
            </span>
          </div>
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-y-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-xs text-on-surface-variant">
              <span className="material-symbols-outlined animate-spin text-2xl text-primary mb-2">progress_activity</span>
              <span>Loading candidate student roster...</span>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-xs text-on-surface-variant">
              <span className="material-symbols-outlined text-3xl mb-1 text-outline-variant">person_off</span>
              <span>No matching students found for allocation</span>
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 border-b border-outline-variant/20 bg-surface-container-low/90 backdrop-blur-xs text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3">Roll No</th>
                  <th className="px-4 py-3">Student Name</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Batch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10 font-medium text-on-surface">
                {filteredStudents.map((s) => {
                  const id = s.studentId ?? s.id
                  const isChecked = selectedIds.has(id)
                  return (
                    <tr
                      key={id}
                      onClick={() => handleToggleOne(id)}
                      className={`cursor-pointer transition-colors ${
                        isChecked ? 'bg-primary/[0.04]' : 'hover:bg-surface-container-low/50'
                      }`}
                    >
                      <td className="px-4 py-2.5">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary pointer-events-none"
                        />
                      </td>
                      <td className="px-4 py-2.5 font-bold text-primary">{s.rollNo || '—'}</td>
                      <td className="px-4 py-2.5 font-semibold text-on-surface">{s.fullName || s.name}</td>
                      <td className="px-4 py-2.5 text-on-surface-variant">{s.branchName || '—'}</td>
                      <td className="px-4 py-2.5 text-on-surface-variant">{s.batchNames || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 pt-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/30 bg-surface px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveAllocation}
            disabled={isSaving || selectedIds.size === 0}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition-all cursor-pointer disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                <span>Saving Allocation...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">check</span>
                <span>Allocate ({selectedIds.size}) Students</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
